/**
 * Auto loan leads (loan_type "auto") come from the Auto Loan Rate Table embed.
 * They carry vehicle_* fields and `term_months` instead of the mortgage
 * property / residency fields, so the lead summary renders them separately.
 */
export const isAutoLoanType = (loanType?: string | null): boolean =>
  loanType?.toLowerCase() === "auto"

const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === "") return 0
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Down payment as a whole-number percent of the vehicle value, e.g. 10.
 * Prefers a stored `down_payment_percent` (rate table value) when present.
 */
export const getAutoDownPaymentPercent = (details: {
  down_payment?: number | string | null
  down_payment_percent?: number | string | null
  vehicle_value?: number | string | null
}): number | null => {
  const stored = toNumber(details.down_payment_percent)
  if (stored > 0) return Math.round(stored)
  const value = toNumber(details.vehicle_value)
  const down = toNumber(details.down_payment)
  return value > 0 && down > 0 ? Math.round((down / value) * 100) : null
}

/** "2022 Toyota Camry" from the workflow's vehicle question, or null. */
export const getAutoLoanVehicle = (details: {
  vehicle_year?: number | string | null
  vehicle_make?: string | null
  vehicle_model?: string | null
}): string | null =>
  [details.vehicle_year, details.vehicle_make, details.vehicle_model]
    .filter(part => part !== undefined && part !== null && part !== "")
    .join(" ") || null

/** Display labels for the workflow's option values (contract defaults). */
const AUTO_LOAN_VALUE_LABELS: Record<string, string> = {
  car_sedan: "Car or sedan",
  suv_crossover: "SUV or crossover",
  van_minivan: "Van or minivan",
  private_seller: "Private seller",
  not_sure: "Not sure yet",
  yes_owe: "Yes, owes money on it",
  yes_paid_off: "Yes, paid off",
  in_1_3_months: "In 1–3 months",
  within_30_days: "Within 30 days",
  cash_out: "Cash out",
  none: "None"
}

const toLabel = (value: string): string =>
  AUTO_LOAN_VALUE_LABELS[value] ??
  value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " ")

const isSet = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== ""

/**
 * The auto loan workflow's extra answers, as label/value rows for the lead
 * summary. Only answers the lead carries (hidden questions are not sent).
 */
export const getAutoLoanAnswerRows = (details: {
  current_rate?: number | string | null
  months_remaining?: number | string | null
  mileage?: number | string | null
  vehicle_type?: string | null
  purchase_source?: string | null
  trade_in?: string | null
  refinance_goal?: string | null
  financing_timeline?: string | null
  repossession_bankruptcy?: string | null
}): { label: string; value: string }[] => {
  const rows: { label: string; value: string }[] = []
  if (isSet(details.current_rate)) {
    rows.push({ label: "Current rate", value: `${details.current_rate}%` })
  }
  if (isSet(details.months_remaining)) {
    rows.push({
      label: "Months remaining",
      value: `${details.months_remaining} Months`
    })
  }
  if (isSet(details.mileage)) {
    rows.push({
      label: "Mileage",
      value: `${toNumber(details.mileage).toLocaleString("en-US")} mi`
    })
  }
  const options: [keyof typeof details, string][] = [
    ["vehicle_type", "Vehicle type"],
    ["purchase_source", "Buying from"],
    ["trade_in", "Trade-in"],
    ["refinance_goal", "Refinance goal"],
    ["financing_timeline", "Financing timeline"],
    ["repossession_bankruptcy", "Repossession / bankruptcy"]
  ]
  options.forEach(([key, label]) => {
    const value = details[key]
    if (isSet(value)) rows.push({ label, value: toLabel(String(value)) })
  })
  return rows
}
